/**
 * Color Mode System
 * Provides light/dark mode support with context and hooks
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// Storage key for persisting color mode
const COLOR_MODE_STORAGE_KEY = 'chakra-ui-color-mode';

// Default color mode
const DEFAULT_COLOR_MODE = 'light';

/**
 * Color Mode Context
 */
const ColorModeContext = createContext({
  colorMode: DEFAULT_COLOR_MODE,
  setColorMode: () => {},
  toggleColorMode: () => {},
});

/**
 * Get initial color mode from storage or default
 */
const getInitialColorMode = (initialColorMode) => {
  if (typeof window === 'undefined') return initialColorMode || DEFAULT_COLOR_MODE;
  
  try {
    const stored = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch (e) {
    // localStorage not available
  }
  
  return initialColorMode || DEFAULT_COLOR_MODE;
};

/**
 * Color Mode Provider
 * Wraps the application to provide color mode context
 */
export const ColorModeProvider = ({ 
  children, 
  initialColorMode = DEFAULT_COLOR_MODE,
  useSystemColorMode = false,
}) => {
  const [colorMode, setColorModeState] = useState(() => 
    getInitialColorMode(initialColorMode)
  );

  // Set color mode and persist to storage
  const setColorMode = useCallback((mode) => {
    setColorModeState(mode);
    try {
      localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
    } catch (e) {
      // localStorage not available
    }
    
    // Update document class for CSS targeting
    if (typeof document !== 'undefined') {
      document.documentElement.classList.remove('light', 'dark');
      document.documentElement.classList.add(mode);
      document.documentElement.setAttribute('data-theme', mode);
    }
  }, []);

  // Toggle between light and dark modes
  const toggleColorMode = useCallback(() => {
    setColorMode(colorMode === 'light' ? 'dark' : 'light');
  }, [colorMode, setColorMode]);

  // Initialize document class on mount
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.classList.remove('light', 'dark');
      document.documentElement.classList.add(colorMode);
      document.documentElement.setAttribute('data-theme', colorMode);
    }
  }, [colorMode]);

  // Listen for system color mode changes if enabled
  useEffect(() => {
    if (!useSystemColorMode || typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      setColorMode(e.matches ? 'dark' : 'light');
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Older browsers
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [useSystemColorMode, setColorMode]);

  const value = {
    colorMode,
    setColorMode,
    toggleColorMode,
  };

  return (
    <ColorModeContext.Provider value={value}>
      {children}
    </ColorModeContext.Provider>
  );
};

/**
 * useColorMode Hook
 * Returns the current color mode and functions to change it
 * 
 * @returns {{ colorMode: 'light' | 'dark', setColorMode: function, toggleColorMode: function }}
 */
export const useColorMode = () => {
  const context = useContext(ColorModeContext);
  if (context === undefined) {
    throw new Error('useColorMode must be used within a ColorModeProvider');
  }
  return context;
};

/**
 * useColorModeValue Hook
 * Returns a value based on the current color mode
 * 
 * @param {any} lightValue - Value to return in light mode
 * @param {any} darkValue - Value to return in dark mode
 * @returns {any} - The appropriate value for current color mode
 * 
 * @example
 * const bg = useColorModeValue('white', 'gray.800');
 * const color = useColorModeValue('gray.800', 'white');
 */
export const useColorModeValue = (lightValue, darkValue) => {
  const { colorMode } = useColorMode();
  return colorMode === 'light' ? lightValue : darkValue;
};

/**
 * LightMode Component
 * Forces light mode for its children
 */
export const LightMode = ({ children }) => {
  return (
    <ColorModeContext.Provider value={{ 
      colorMode: 'light', 
      setColorMode: () => {}, 
      toggleColorMode: () => {} 
    }}>
      {children}
    </ColorModeContext.Provider>
  );
};

/**
 * DarkMode Component
 * Forces dark mode for its children
 */
export const DarkMode = ({ children }) => {
  return (
    <ColorModeContext.Provider value={{ 
      colorMode: 'dark', 
      setColorMode: () => {}, 
      toggleColorMode: () => {} 
    }}>
      {children}
    </ColorModeContext.Provider>
  );
};

/**
 * Mode utility function (for theme component styles)
 * Returns a function that selects values based on color mode
 * Compatible with Chakra's theme-tools mode function
 * 
 * @param {any} light - Light mode value
 * @param {any} dark - Dark mode value
 * @returns {function} - Function that takes props and returns the appropriate value
 */
export const mode = (light, dark) => (props) => {
  const colorMode = props.colorMode || 'light';
  return colorMode === 'light' ? light : dark;
};

export { ColorModeContext };
